import { Polygon, FeatureCollection, Feature } from '@turf/turf';
import { AuthorizableLAANCAdvisoryProperties } from './advisories/AuthorizableLAANCAdvisoryProperties';
import { IAdvisoryGeometry, IAdvisoryPropertiesV3 } from './advisories/IAdvisoryPropertiesV3';
import { ILAANCStatusMeta, IPersistableAuthorizableLAANCAdvisoryProperties } from './advisories/IAuthorizableLAANCAdvisoryProperties';
import { ICoordinate } from './ICoordinate';
import { ISubdividableMissionProperties } from './ISubdividableMissionProperties';

// changing this will break all consumers, especially be mindful of ionic staging, where that likely
// will be broken for a while since we don't usually deploy to ionic staging until acceptance / late in the story
const version = '7';

// this interface doesn't have any methods it is meant to be an interface for what goes over the "wire"
export interface IPersistableMissionFeatureCollection  extends FeatureCollection<IAdvisoryGeometry, IAdvisoryPropertiesV3 | ISubdividableMissionProperties> {
    properties: { version }
}
/**
 * version: 2
 *  - this was a breaking change due to persisting all the polygons
 * version: 3
 *  - this was a breaking change b/c we no longer store canIFly and we are putting the radius in the features
 * version: 4
 *  - this was a breaking change where we completely refactored laanc statuses
 * version: 5
 *  - this was a breaking change as the laanc composite is no longer included in the feature (so a client that understands V4 will break on V5 expecting composite to be there)
 * version: 6
 *  - this was a breaking change since IAuthorizableLAANCAdvisoryProperties now requires a start and end time
 * version: 7
 *  - all authorizations now have a status history
 */
export class PersistableMissionFeatureCollection implements IPersistableMissionFeatureCollection {
    type: 'FeatureCollection' = 'FeatureCollection';
    properties = { version };
    features: Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3 | ISubdividableMissionProperties>[];

    constructor({ features, properties }: { features: Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3 | ISubdividableMissionProperties>[], properties?: any }) {
        if (properties && properties.version !== version) {
            throw new Error(`attempted to deserialize a mission with version ${properties.version} which is not supported`);
        }
        this.features = features.map(f => {
            if (f.properties.source === 'uasfm+authorizable'){
                const statusMeta: ILAANCStatusMeta = {};
                f.properties = new AuthorizableLAANCAdvisoryProperties(f.properties as IPersistableAuthorizableLAANCAdvisoryProperties, statusMeta);
            }
            return f;
        });
    }

    getMissionRadius = (): Feature<Polygon, ISubdividableMissionProperties> => {
        return this.features.find(f => f.properties.source === 'mission-radius') as Feature<Polygon, ISubdividableMissionProperties>;
    }

    getMissionCenter = (): ICoordinate => {
        return this.getMissionRadius().properties.center;
    }

    getMissionRadiusMeters = (): number => {
        return this.getMissionRadius().properties.radiusMeters;
    }

    getAdvisoryFeatures = (): Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[] => {
        return this.features.filter(f => f.properties.source !== 'mission-radius') as Feature<IAdvisoryGeometry, IAdvisoryPropertiesV3>[];
    }

    getAdvisories = (): IAdvisoryPropertiesV3[] => {
        return this.getAdvisoryFeatures().map(f => f.properties);
    }

    getLAANCAuthorizeableAdvisoryFeatures = (): Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[] => {
        return this.getAdvisoryFeatures()
            .filter(a => a.properties.source === 'uasfm+authorizable') as Feature<Polygon, AuthorizableLAANCAdvisoryProperties>[];
    }
}
